/**
 * IMPORTANT DATE HANDLING PATTERN:
 * 
 * This application stores dates using four separate fields to avoid timezone issues:
 * - date: JavaScript Date object (for MongoDB Date type)
 * - year: Integer year (e.g., 2024)
 * - month: Integer 1-12 (human-readable, NOT 0-11 like JS)
 * - day: Integer day of month
 * 
 * Key rules:
 * 1. Always store all four fields together
 * 2. DB month is 1-12, JS Date month is 0-11
 * 3. When creating Date from DB: new Date(year, month - 1, day)
 * 4. When saving to DB: { date: value.toDate(), year, month: jsMonth + 1, day }
 * 5. Use momentFromEntry() and datePartsFromMoment() helpers
 */

import React, { useEffect, useState, useRef, useContext, useMemo } from 'react';
import { Button, Form, Input, InputNumber, Menu, TreeSelect, DatePicker as AntDatePicker } from 'antd';
import styled from 'styled-components';

import withAuth from '~/libs/withAuth';
import { ContentLayout, MainBlockLayout } from '~/components/layouts';

import imgAssistantRent from '~/public/assistant-rent.svg';
import imgAssistantBank from '~/public/assistant-bank.svg';
import Image from 'next/image';
import { datePartsFromMoment, momentFromEntry, moneyFormatter, nodesTitleResolver, paymentMethodResolver } from '~/libs/helpers';
import { mutate } from 'swr';
import { StyledTable } from '~/components/styles/StyledTable';
import useGlobalState from '~/libs/context';

const StyledAssistant = styled.div`
  background: white;
  padding: 20px;

  .assistant-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h2 {
      margin: 0;
    }
  }

  .left-column {
    width: 180px;
    height: 100%;
    padding: 10px;
    margin-right: 20px;
    background: #f5f5f5;
    height: 100%;
  }

  .content-column {
    flex: 1;
    margin-right: 20px;

    &:last-child {
      margin-right: 0;
    }
  }
`;

const Rent = ({ assistant, addAssistant, updateAssistant, settings, onBack }) => {
  const { user, year } = useGlobalState();
  const EditableContext = React.createContext(null);
  const [ busy, setBusy ] = useState(false);
  const [ editMode, setEditMode ] = useState(false);
  const [ rentData, setRentData ] = useState();
  const [ editData, setEditData ] = useState([]);
  const paymentMethods = settings?.paymentMethods || [];

  useEffect(() => { setEditData(rentData) }, [rentData]);

  const isDifferent = useMemo(() => {
    return JSON.stringify(rentData) !== JSON.stringify(editData);
  }, [rentData, editData]);

  useEffect(() => {
    // Load the assistant data for this year and for this moduleId
    if (!assistant) {
      const monthlyPayments = [];
      for (let i = 0; i < 12; i++) {
        monthlyPayments.push({
          key: i,
          month: i + 1,
          amount: null,
          paidOn: null,  // Will store {date, year, month, day} when set
          deductible: 10,
          paidWith: '',
        });
      }
      const punctualPayments = [];
      addAssistant({ monthlyPayments, punctualPayments });
    }
  }, []);

  useEffect(() => {
    if (assistant) {
      setRentData(assistant.data);
    }
  }, [assistant]);

  let columns = [
    { title: 'Month', dataIndex: 'month', key: 'month', width: 20,
      render: x => x ? (x.toString()) + '月' : ''  },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', editable: true, width: 60,
      render: x => x ? moneyFormatter(x) : <small style={{ color: 'lightgray' }}>N/A</small> },
    { title: '%', dataIndex: 'percentDeductible', key: 'percentDeductible', editable: true, width: 5,
      render: x => x ? x + '%' : '' },
    { title: 'Paid On', dataIndex: 'paidOn', key: 'paidOn', width: 70, editable: true,
      render: x => x && x.year && x.month && x.day ? `${x.year}/${x.month}/${x.day}` : ''
    },
    { title: 'Paid With', dataIndex: 'paidWith', key: 'paidWith', width: 60, editable: true,
      render: x => paymentMethodResolver(x, paymentMethods),
    },
  ];

  const onMonthlyRateRowUpdate = (row) => {
    const newData = [...editData.monthlyPayments];
    const index = newData.findIndex((item) => row.month === item.month);
    const item = newData[index];
    
    // Update the current row first
    const updatedRow = { ...item, ...row };
    newData.splice(index, 1, updatedRow);
    
    // Handle amount field changes
    if (row.hasOwnProperty('amount')) {
      if (!updatedRow.amount) {
        // Clear all fields when amount is removed
        updatedRow.percentDeductible = null;
        updatedRow.paidWith = '';
        updatedRow.paidOn = null;
        newData[index] = updatedRow;
      } else if (updatedRow.amount && index > 0) {
        // Auto-fill logic when amount is added and row has previous row
        const prevRow = newData[index - 1];
        
        // Auto-fill current row if fields are empty
        if (!updatedRow.percentDeductible && prevRow.percentDeductible) {
          updatedRow.percentDeductible = prevRow.percentDeductible;
          newData[index] = updatedRow;
        }
        
        if (!updatedRow.paidWith && prevRow.paidWith) {
          updatedRow.paidWith = prevRow.paidWith;
          newData[index] = updatedRow;
        }
        
        if (!updatedRow.paidOn && prevRow.paidOn && prevRow.paidOn.year) {
          updatedRow.paidOn = {
            date: new Date(prevRow.paidOn.year, prevRow.paidOn.month, prevRow.paidOn.day),
            year: prevRow.paidOn.year,
            month: prevRow.paidOn.month + 1,
            day: prevRow.paidOn.day
          };
          
          // Handle year rollover
          if (updatedRow.paidOn.month > 12) {
            updatedRow.paidOn.year += 1;
            updatedRow.paidOn.month = 1;
            updatedRow.paidOn.date = new Date(updatedRow.paidOn.year, 0, updatedRow.paidOn.day);
          }
          
          newData[index] = updatedRow;
        }
      }
    }
    
    setEditData({ ...editData, monthlyPayments: newData });
  }

  columns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave: onMonthlyRateRowUpdate,
      }),
    };
  });

  const punctualColumns = [
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 60,
      render: x => x ? moneyFormatter(x) : ''
    },
    { title: '%', dataIndex: 'percentDeductible', key: 'percentDeductible', width: 10,
      render: x => x ? x + '%' : ''
    },
    { title: 'Paid On', dataIndex: 'paidOn', key: 'paidOn', width: 70 },
    { title: 'Paid With', dataIndex: 'paidWith', key: 'paidWith', width: 60,
      render: x => paymentMethodResolver(x, paymentMethods),
    },
  ];

  const EditableRow = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
      <Form form={form} component={false}>
        <EditableContext.Provider value={form}>
          <tr {...props} />
        </EditableContext.Provider>
      </Form>
    );
  };

  const EditableCell = ({ title, editable, children, dataIndex, record, handleSave, ...restProps }) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);
    const form = useContext(EditableContext);
    const value = record ? record[dataIndex] : null;

    useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); } }, [ editing ]);

    const toggleEdit = () => {
      setEditing(!editing);
      if (dataIndex === 'paidOn') {
        const dateValue = record[dataIndex];
        form.setFieldsValue({ [dataIndex]: dateValue && dateValue.year ? momentFromEntry(dateValue) : null });
      } else {
        form.setFieldsValue({ [dataIndex]: record[dataIndex] });
      }
    };

    const save = async () => {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    };

    if (!editing) {
      return (
        <td {...restProps}>
          <div style={{ cursor: editable ? 'pointer' : 'default', padding: '5px 10px' }} 
            onClick={editable ? toggleEdit : null}>
            {children}
          </div>
        </td>
      );
    }

    /* PAYMENT METHODS */
    const mapPaymentMethodToTree = (items) => {
      return items.map(x => ({
        key: x._id,
        value: x._id,
        //disabled: x.name === 'personal-advance' && expense.personal,
        title: nodesTitleResolver(x._id, paymentMethods),
        items: (x.items && x.items.length > 0) ? mapPaymentMethodToTree(x.items) : null
      }));
    }
    let paymentMethodData = mapPaymentMethodToTree(paymentMethods);

    if (dataIndex === 'paidOn') {
      return (<td {...restProps}>
        <Form.Item style={{ margin: 0, }} name={dataIndex} initialValue={value && value.year ? momentFromEntry(value) : null}>
          <AntDatePicker allowClear={false}
            onChange={(newValue) => {
              if (newValue) {
                const { year, month, day } = datePartsFromMoment(newValue);
                form.setFieldsValue({ [dataIndex]: newValue });
                // IMPORTANT: Store date with all four fields (date, year, month, day)
                // This pattern prevents timezone issues and allows efficient DB queries
                handleSave({ ...record, paidOn: { 
                  date: newValue.toDate(),  // JavaScript Date object for MongoDB
                  year,                     // Integer year (e.g., 2024)
                  month,                    // Integer 1-12 (NOT 0-11 like JS Date)
                  day                       // Integer day of month
                } });
              }
              toggleEdit();
            }}
          />
        </Form.Item>
      </td>);
    }

    if (dataIndex === 'paidWith') {
      return (<td {...restProps}>
        <Form.Item style={{ margin: 0, }} name={dataIndex}>
          <TreeSelect treeData={paymentMethodData}
            showSearch={true} treeDefaultExpandAll={true}
            filterTreeNode={(input, treeNode) => {
              input = input.toLowerCase();
              return treeNode.title.toLowerCase().includes(input);
            }}
            onPressEnter={save} onBlur={save}>
          </TreeSelect>
        </Form.Item>
      </td>);
    }

    // Standard Input Field
    return (<td {...restProps}>
      <Form.Item style={{ margin: 0, }} name={dataIndex}>
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    </td>);
  };

  const onCancel = () => {
    setEditData(rentData);
    setEditMode(false);
  }

  const onSave = async () => {
    setBusy(true);
    await updateAssistant(editData);
    // Invalidate journal cache to refresh journal entries
    mutate('/api/journal');
    mutate(`/api/journal?year=${year}`);
    setEditMode(false);
    setBusy(false);
    // Navigate back to assistants list
    onBack();
  }

	return (
    <StyledAssistant>

      <div className="assistant-header">
        <h2>Rent</h2>
        <div>
          <Button style={{ marginRight: 5 }} onClick={onBack} disabled={busy}>Back</Button>
          {editMode && <Button style={{ marginRight: 5 }} onClick={onCancel} disabled={busy}>Cancel</Button>}
          <Button type="primary" onClick={onSave} loading={busy} disabled={!isDifferent}>Save</Button>
        </div>
      </div>

      <div style={{ display: 'flex' }}>
        <div className="left-column">
          <Image alt="Rent" src={imgAssistantRent} style={{ width: 60, height: 'auto' }} />
        </div>
        <div className="content-column" style={{ width: '100%' }}>
          <h3>Monthly Rent</h3>
          <StyledTable rowKey='_id' size='small'
            columns={columns}
            components={{ body: { row: EditableRow, cell: EditableCell } }}
            dataSource={editData?.monthlyPayments}
            pagination={{ pageSize: 50 }}
            //loading={busyLoading || busyDeleting} 
          />
        </div>
      </div>

      <div style={{ marginTop: '20px' }}>
        <h3>Punctual Payments</h3>
        <StyledTable rowKey='_id' size='small' 
          columns={punctualColumns}
          dataSource={editData?.punctualPayments}
          pagination={{ pageSize: 50 }}
          //loading={busyLoading || busyDeleting} 
        />
      </div>
    </StyledAssistant>
	);
};

export default withAuth(Rent);